import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Select, { components } from "react-select";
import { useNavigate } from "react-router-dom";
import i18next from "i18next";
import { Store } from "../../../flux";
import { searchVocabs } from "../../../services/api/searchVocabAPI";

const SearchInput = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [searchInput, setSearchInput] = useState('');
  const [openMenu, setOpenMenu] = useState(false);
  const currentLanguage = i18next.language;
  const debounceRef = useRef(null);
  const isMounted = useRef(true);
  const latestRequestIdRef = useRef(0);
  const queryCacheRef = useRef({});
  const queryCacheOrderRef = useRef([]);

  const MIN_SEARCH_LENGTH = 2;
  const DEBOUNCE_DELAY = 500;
  const CACHE_TTL = 1000 * 60 * 2; // 2 minutes
  const MAX_CACHE_ENTRIES = 20; // cache size

  const clearSearchCache = () => {
    queryCacheRef.current = {};
    queryCacheOrderRef.current = [];
  };

  const cacheResults = (query, results) => {
    if (!query) return;
    const cacheKey = `${currentLanguage}:${query}`;
    queryCacheRef.current[cacheKey] = {
      timestamp: Date.now(),
      results,
    };
    queryCacheOrderRef.current = [cacheKey, ...queryCacheOrderRef.current.filter(key => key !== cacheKey)];
    if (queryCacheOrderRef.current.length > MAX_CACHE_ENTRIES) {
      const oldestKey = queryCacheOrderRef.current.pop();
      delete queryCacheRef.current[oldestKey];
    }
  };

  const getCachedResults = (query) => {
    if (!query) return null;

    const cacheKey = `${currentLanguage}:${query}`;
    const cached = queryCacheRef.current[cacheKey];
    if (!cached) return null;

    if (Date.now() - cached.timestamp >= CACHE_TTL) {
      queryCacheOrderRef.current = queryCacheOrderRef.current.filter((key) => key !== cacheKey);
      delete queryCacheRef.current[cacheKey];
      return null;
    }

    return cached.results;
  };

  const searchSpecificTerm = async (query) => {
    if (!query || query.length < MIN_SEARCH_LENGTH) {
      if (isMounted.current) setOptions([]);
      return;
    }

    const cachedOptions = getCachedResults(query);
    if (cachedOptions) {
      if (isMounted.current) setOptions(cachedOptions);
      return;
    }

    const requestId = ++latestRequestIdRef.current;

    try {
      if (isMounted.current) setLoading(true);
      const results = await searchVocabs(query);

      if (isMounted.current && requestId === latestRequestIdRef.current) {
        setOptions(results);
        cacheResults(query, results);
      }
    } catch (err) {
      console.error("Search fetch error:", err);
      if (isMounted.current && requestId === latestRequestIdRef.current) setOptions([]);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handleInputChange = (input, { action }) => {
    if (action !== "input-change") return;
    setSearchInput(input);
    setOpenMenu(true);

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchSpecificTerm(input), DEBOUNCE_DELAY);
  };

  const handleSelectChange = (selected) => {
    if (!selected) return;

    const groupCategory = selected.groupCategory.split(",")[0];
    const [groupRaw, categoryRaw] = groupCategory.split("/");
    const group = Store.formatString(groupRaw);
    const category = Store.formatString(categoryRaw);

    // Use full word in :vocab, encoded
    const vocabParam = encodeURIComponent(selected.word);
    navigate(`/groups/${group}/${category}/${vocabParam}`);
    setOpenMenu(false);
  };

  useEffect(() => {
    isMounted.current = true;
    clearSearchCache();

    const handleSearchDataUpdated = () => clearSearchCache();
    window.addEventListener("searchDataUpdated", handleSearchDataUpdated);

    return () => {
      isMounted.current = false;
      clearTimeout(debounceRef.current);
      window.removeEventListener("searchDataUpdated", handleSearchDataUpdated);
    };
  }, [currentLanguage]);

  const Menu = (props) => <components.Menu {...props}>{props.children}</components.Menu>;

  return (
    <div className="search-bar">
      <form>
        <Select
          options={options}
          onChange={handleSelectChange}
          isLoading={loading}
          getOptionLabel={(option) => (
            <strong className="text-m-2">
              {currentLanguage === "en" ? option.word : option.perkataan}
            </strong>
          )}
          getOptionValue={(option) => currentLanguage === "en" ? option.word : option.perkataan}
          onInputChange={handleInputChange}
          onBlur={() => setOpenMenu(false)}
          menuIsOpen={openMenu}
          value={null}
          placeholder={loading ? t("loading") : t("search_placeholder")}
          noOptionsMessage={() => loading ? t("loading") : t("no_results")}
          filterOption={(option, input) => {
            const label = currentLanguage === "en" ? option.data.word : option.data.perkataan;
            return label.toLowerCase().includes(input.toLowerCase());
          }}
          components={{ Menu }}
        />
      </form>
    </div>
  );
};

export default SearchInput;
